import React from "react";
import { useIsMobile } from "../../../foundation/altzero_base/base/components/ui/use-mobile";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../foundation/altzero_base/base/components/ui/table";
import { Card, CardContent } from "../../../foundation/altzero_base/base/components/ui/card";

interface Column<T> {
  key: keyof T | string;
  label: string;
  render?: (item: T) => React.ReactNode;
  hideOnMobile?: boolean;
}

interface ResponsiveTableProps<T> {
  data: T[];
  columns: Column<T>[];
  actions?: (item: T) => React.ReactNode;
  mobileCardRender: (item: T) => React.ReactNode;
  emptyMessage?: string;
}

export function ResponsiveTable<T extends { id: string }>({
  data,
  columns,
  actions,
  mobileCardRender,
  emptyMessage = "No data available",
}: ResponsiveTableProps<T>) {
  const isMobile = useIsMobile();

  if (data.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{emptyMessage}</p>
      </div>
    );
  }

  // Mobile: Card view
  if (isMobile) {
    return (
      <div className="space-y-4">
        {data.map((item) => (
          <Card key={item.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              {mobileCardRender(item)}
              {actions && (
                <div className="mt-4 flex gap-2 justify-end border-t pt-4">
                  {actions(item)}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Desktop: Table view
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50">
            {columns.map((col) => (
              <TableHead key={String(col.key)} className="font-semibold">
                {col.label}
              </TableHead>
            ))}
            {actions && <TableHead className="font-semibold">Actions</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((item) => (
            <TableRow key={item.id} className="hover:bg-gray-50">
              {columns.map((col) => (
                <TableCell key={String(col.key)}>
                  {col.render
                    ? col.render(item)
                    : String(item[col.key as keyof T] || "-")}
                </TableCell>
              ))}
              {actions && (
                <TableCell>
                  <div className="flex gap-2">{actions(item)}</div>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

